/**
 * Unified Storage System
 * Centralizes all localStorage operations to avoid conflicts
 */

import { authEventBus } from './auth-event-bus';

interface StoredTokens {
  accessToken: string;
  refreshToken?: string;
}

interface StorageDebugInfo {
  operation: string;
  key: string;
  value?: any;
  timestamp: number;
  stack?: string;
}

class UnifiedStorage {
  private static instance: UnifiedStorage;
  private debugMode = true;
  private history: StorageDebugInfo[] = [];
  
  // Unified keys - all storage operations use these keys only
  private readonly KEYS = {
    AUTH_TOKENS: 'queue_auth_tokens',
    EVENT_ID: 'queue_event_id',
    USER: 'queue_user',
    DEBUG_HISTORY: 'queue_debug_history'
  } as const;

  private constructor() {
    this.initializeDebug();
    this.migrateOldKeys();
  }

  static getInstance(): UnifiedStorage {
    if (!UnifiedStorage.instance) {
      UnifiedStorage.instance = new UnifiedStorage();
    }
    return UnifiedStorage.instance;
  }

  // Migration from old keys to new unified keys
  private migrateOldKeys() {
    const migrations = [
      { old: 'auth_tokens', new: this.KEYS.AUTH_TOKENS },
      { old: 'dooevents_token', new: this.KEYS.AUTH_TOKENS },
      { old: 'current_event_id', new: this.KEYS.EVENT_ID },
      { old: 'currentEventId', new: this.KEYS.EVENT_ID },
      { old: 'dooevents_user', new: this.KEYS.USER },
      { old: 'user', new: this.KEYS.USER }
    ];

    migrations.forEach(({ old: oldKey, new: newKey }) => {
      const oldValue = localStorage.getItem(oldKey);
      if (oldValue && !localStorage.getItem(newKey)) {
        this.log(`Migrating ${oldKey} to ${newKey}`);
        localStorage.setItem(newKey, oldValue);
        localStorage.removeItem(oldKey);
      }
    });
  }

  private initializeDebug() {
    // Load debug history from storage
    const savedHistory = localStorage.getItem(this.KEYS.DEBUG_HISTORY);
    if (savedHistory) {
      try {
        this.history = JSON.parse(savedHistory);
      } catch (e) {
        this.history = [];
      }
    }

    // Add global debug object
    (window as any).storage = {
      show: () => this.showAll(),
      history: () => this.showHistory(),
      clear: () => this.clearAll(),
      test: () => this.testStorage(),
      keys: () => Object.keys(this.KEYS),
      get: (key: string) => this.getRaw(key),
      set: (key: string, value: string) => this.setRaw(key, value)
    };

    console.log('🔧 Unified Storage initialized. Use window.storage for debug.');
  }

  private log(message: string, data?: any) {
    if (this.debugMode) {
      console.log(`[UnifiedStorage] ${message}`, data || '');
    }
  }

  private recordOperation(operation: string, key: string, value?: any) {
    const info: StorageDebugInfo = {
      operation,
      key,
      value: value ? JSON.stringify(value).substring(0, 100) : undefined,
      timestamp: Date.now(),
      stack: new Error().stack
    };
    
    this.history.push(info);
    
    // Keep only last 50 operations
    if (this.history.length > 50) {
      this.history = this.history.slice(-50);
    }
    
    // Save history to localStorage
    try {
      localStorage.setItem(this.KEYS.DEBUG_HISTORY, JSON.stringify(this.history));
    } catch (e) {
      console.error('Failed to save debug history:', e);
    }
  }

  // Token operations
  setTokens(tokens: StoredTokens) {
    this.log('Setting tokens', { hasAccess: !!tokens.accessToken, hasRefresh: !!tokens.refreshToken });
    this.recordOperation('SET_TOKENS', this.KEYS.AUTH_TOKENS, tokens);
    
    // Get previous tokens for comparison
    const previousTokens = this.getTokens();
    const isNewToken = previousTokens?.accessToken !== tokens.accessToken;
    
    try {
      localStorage.setItem(this.KEYS.AUTH_TOKENS, JSON.stringify(tokens));
      this.log('Tokens saved successfully');
      
      // Verify immediately
      const saved = localStorage.getItem(this.KEYS.AUTH_TOKENS);
      if (!saved) {
        console.error('CRITICAL: Tokens were not saved to localStorage!');
      } else {
        this.log('Verified: Tokens are in localStorage');
      }
      
      // Emit event for token update
      if (isNewToken) {
        authEventBus.emit('token-updated', { 
          newToken: tokens.accessToken,
          hadPreviousToken: !!previousTokens?.accessToken 
        });
        this.log('Token update event emitted');
      }
      
      return true;
    } catch (error) {
      console.error('Failed to save tokens:', error);
      this.recordOperation('SET_TOKENS_ERROR', this.KEYS.AUTH_TOKENS, error);
      authEventBus.emit('auth-error', { error: 'Failed to save tokens' });
      return false;
    }
  }

  getTokens(): StoredTokens | null {
    this.recordOperation('GET_TOKENS', this.KEYS.AUTH_TOKENS);
    
    try {
      const stored = localStorage.getItem(this.KEYS.AUTH_TOKENS);
      if (!stored) {
        this.log('No tokens found');
        return null;
      }
      
      const tokens = JSON.parse(stored);
      this.log('Tokens retrieved', { hasAccess: !!tokens.accessToken });
      return tokens;
    } catch (error) {
      console.error('Failed to get tokens:', error);
      this.recordOperation('GET_TOKENS_ERROR', this.KEYS.AUTH_TOKENS, error);
      return null;
    }
  }

  clearTokens() {
    this.log('Clearing tokens');
    this.recordOperation('CLEAR_TOKENS', this.KEYS.AUTH_TOKENS);
    localStorage.removeItem(this.KEYS.AUTH_TOKENS);
    
    // Emit logout event
    authEventBus.emit('logout', { reason: 'tokens-cleared' });
    this.log('Logout event emitted');
  }

  // Event ID operations
  setEventId(eventId: string) {
    this.log('Setting event ID', eventId);
    this.recordOperation('SET_EVENT_ID', this.KEYS.EVENT_ID, eventId);
    
    // Get previous eventId for comparison
    const previousEventId = this.getEventId();
    const isNewEventId = previousEventId !== eventId;
    
    try {
      localStorage.setItem(this.KEYS.EVENT_ID, eventId);
      
      // Verify immediately
      const saved = localStorage.getItem(this.KEYS.EVENT_ID);
      if (saved !== eventId) {
        console.error('CRITICAL: Event ID was not saved correctly!');
      } else {
        this.log('Verified: Event ID is in localStorage');
      }
      
      // Emit event for eventId change
      if (isNewEventId) {
        authEventBus.emit('eventId-changed', {
          newEventId: eventId,
          previousEventId: previousEventId
        });
        this.log('EventId change event emitted');
      }
      
      return true;
    } catch (error) {
      console.error('Failed to save event ID:', error);
      authEventBus.emit('auth-error', { error: 'Failed to save eventId' });
      return false;
    }
  }

  getEventId(): string | null {
    this.recordOperation('GET_EVENT_ID', this.KEYS.EVENT_ID);
    const eventId = localStorage.getItem(this.KEYS.EVENT_ID);
    this.log('Retrieved event ID', eventId);
    return eventId;
  }

  clearEventId() {
    this.log('Clearing event ID');
    this.recordOperation('CLEAR_EVENT_ID', this.KEYS.EVENT_ID);
    localStorage.removeItem(this.KEYS.EVENT_ID);
  }

  // User operations
  setUser(user: any) {
    this.log('Setting user', user);
    this.recordOperation('SET_USER', this.KEYS.USER, user);
    
    try {
      localStorage.setItem(this.KEYS.USER, JSON.stringify(user));
      return true;
    } catch (error) {
      console.error('Failed to save user:', error);
      return false;
    }
  }

  getUser(): any {
    this.recordOperation('GET_USER', this.KEYS.USER);
    
    try {
      const stored = localStorage.getItem(this.KEYS.USER);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Failed to get user:', error);
      return null;
    }
  }

  clearUser() {
    this.log('Clearing user');
    this.recordOperation('CLEAR_USER', this.KEYS.USER);
    localStorage.removeItem(this.KEYS.USER);
  }

  // Clear all
  clearAll() {
    this.log('Clearing all storage');
    this.recordOperation('CLEAR_ALL', 'ALL');
    
    Object.values(this.KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  }

  // Debug methods
  showAll() {
    console.log('=== Unified Storage Contents ===');
    console.log('Total localStorage items:', localStorage.length);
    
    // Show our keys
    console.log('\n📦 Queue Storage Keys:');
    Object.entries(this.KEYS).forEach(([name, key]) => {
      const value = localStorage.getItem(key);
      console.log(`  ${name} (${key}):`, value ? value.substring(0, 100) + '...' : 'null');
    });
    
    // Show other keys
    console.log('\n🔍 Other Keys in localStorage:');
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !Object.values(this.KEYS).includes(key as any)) {
        const value = localStorage.getItem(key);
        console.log(`  ${key}:`, value ? value.substring(0, 50) + '...' : 'null');
      }
    }
    
    console.log('================================');
  }

  showHistory() {
    console.log('=== Storage Operation History ===');
    this.history.slice(-20).forEach(op => {
      const time = new Date(op.timestamp).toLocaleTimeString();
      console.log(`${time} - ${op.operation} [${op.key}]`, op.value || '');
    });
    console.log('=================================');
  }

  testStorage() {
    console.log('🧪 Testing localStorage...');
    
    const testKey = '__test__';
    const testValue = 'test_' + Date.now();
    
    try {
      // Test direct localStorage
      localStorage.setItem(testKey, testValue);
      const retrieved = localStorage.getItem(testKey);
      localStorage.removeItem(testKey);
      
      if (retrieved === testValue) {
        console.log('✅ Direct localStorage: WORKING');
      } else {
        console.error('❌ Direct localStorage: FAILED');
      }
      
      // Test unified storage
      const testTokens = { accessToken: 'test_token_' + Date.now() };
      this.setTokens(testTokens);
      const savedTokens = this.getTokens();
      
      if (savedTokens?.accessToken === testTokens.accessToken) {
        console.log('✅ Unified token storage: WORKING');
      } else {
        console.error('❌ Unified token storage: FAILED');
      }
      
      // Check if data persists
      console.log('\n📊 Current state:');
      this.showAll();
      
    } catch (error) {
      console.error('❌ Storage test failed:', error);
    }
  }

  // Raw access for debugging
  private getRaw(key: string): string | null {
    return localStorage.getItem(key);
  }

  private setRaw(key: string, value: string) {
    localStorage.setItem(key, value);
    console.log(`Set ${key} = ${value}`);
  }
}

// Export singleton instance
export const unifiedStorage = UnifiedStorage.getInstance();

// Export for type usage
export type { StoredTokens };